import React from "react";
import {useLocation} from "react-router-dom";
import {SomethingWrong, codeIncident} from "../components/organisms/SomethingWrong";
import {signalerErreur} from "./signalerErreur";

/**
 * Ce qu'une frontiere React attrape, et ce qu'elle ne verra jamais.
 *
 * `componentDidCatch` recoit l'erreur — nom, message, pile — et la pile des
 * composants, qui dit quel composant a lance et toute sa lignee. Ce n'est pas
 * aveugle : c'est simplement ce qu'on jetait jusqu'ici.
 *
 * En revanche elle ne voit que ce qui est lance **pendant le rendu**, dans les
 * methodes de cycle de vie et les constructeurs. Un `onClick` qui plante, une
 * promesse rejetee, un appel reseau qui echoue lui passent a cote — ceux-la
 * demandent `window.onerror` et `window.onunhandledrejection`, qui sont un
 * autre sujet.
 *
 * Un cas est reconnaissable et merite un autre discours : le `ChunkLoadError`
 * d'un import paresseux, qui survient quand le navigateur demande un fichier
 * qu'un deploiement vient de remplacer. Celui-la, recharger le repare vraiment.
 * Tous les autres, non — et l'ecran ne doit pas promettre le contraire.
 *
 * ---
 *
 * Une frontiere doit occuper l'espace de ce qui a echoue, et proposer ce qui a
 * une chance d'aider a cet endroit. D'ou trois niveaux, et pas un seul :
 *
 *   bloc         un composant dans une page — une tuile de tableau de bord, une
 *                carte, un tableau. Le reste de la page est intact et
 *                l'utilisateur est en train de faire autre chose : on tient dans
 *                la place perdue et on ne propose que de reessayer. Ni
 *                navigation ni rechargement : interrompre toute la page pour une
 *                tuile serait disproportionne.
 *
 *   page         l'ecran d'une route. La navigation est rendue au-dessus de la
 *                frontiere, donc elle survit : on peut proposer de partir
 *                ailleurs, de reessayer sans recharger, ou de recharger.
 *
 *   application  la racine, au-dessus du routeur. Si on en arrive la, la
 *                navigation est tombee aussi : proposer « retour au tableau de
 *                bord » serait un bouton qui ne marche pas. Recharger est la
 *                seule chose qui reste vraie.
 */
class ErrorBoundaryInner extends React.Component {
    constructor(props) {
        super(props);
        this.state = {error: null, info: null};
    }

    componentDidCatch(error, info) {
        this.setState({error: error, info: info});
        // Trace locale, lisible sur l'appareil de qui rencontre le probleme.
        // L'envoi vers healthz viendra se brancher ici.
        try {
            console.error("[boundary]", {
                niveau: this.props.niveau || "page",
                bloc: this.props.nom,
                name: error?.name,
                message: error?.message,
                stack: error?.stack,
                componentStack: info?.componentStack,
                route: window.location.pathname,
            });
        } catch (e) {
            // Une frontiere qui plante en journalisant ne protege plus rien.
        }

        // Le code est celui que l'utilisateur lit a l'ecran : on l'envoie avec,
        // pour pouvoir relier sa capture d'ecran a la ligne enregistree.
        signalerErreur({
            niveau: this.props.niveau || "page",
            error: error,
            bloc: this.props.nom,
            componentStack: info?.componentStack,
            code: codeIncident(error),
        });
    }

    reset = () => {
        this.setState({error: null, info: null});
    }

    render() {
        if (this.state.error) {
            return <SomethingWrong
                error={this.state.error}
                retry={this.reset}
                niveau={this.props.niveau || "page"}
                nom={this.props.nom}
            />;
        }
        return this.props.children;
    }
}

/**
 * La frontiere se remonte a chaque changement de route.
 *
 * Une frontiere React ne se reinitialise jamais d'elle-meme : `error` passe a
 * une valeur et y reste. Sans cette cle, proposer une sortie ne servirait a
 * rien — l'URL changerait, l'ecran d'erreur resterait. C'est la precaution qui
 * rend possible le « ou voulez-vous aller » demande depuis 2025.
 */
/**
 * @param {{children?:React.ReactNode, niveau?:"bloc"|"page"|"application", nom?:string}} props
 */
export function ErrorBoundary({children, niveau = "page", nom}) {
    const location = useLocation();
    return (
        <ErrorBoundaryInner key={location.pathname} niveau={niveau} nom={nom}>
            {children}
        </ErrorBoundaryInner>
    );
}

/**
 * La meme, hors du routeur.
 *
 * Les frontieres de l'application sont posees a l'interieur des routes, donc
 * sous le layout : un plantage du layout lui-meme, ou au-dessus, n'etait
 * attrape par personne et donnait une page blanche muette. Celle-ci est le
 * dernier filet, et elle ne peut compter sur aucune navigation.
 */
export class RootErrorBoundary extends ErrorBoundaryInner {
    // Le niveau passe par les props par defaut, pour que la trace de
    // componentDidCatch dise « application » elle aussi et non « page ».
    static defaultProps = {niveau: "application"};

    render() {
        if (this.state.error) {
            return <SomethingWrong error={this.state.error} niveau={"application"}/>;
        }
        return this.props.children;
    }
}
